/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, useRef } from "react";
import InputField from "components/fields/InputField";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { Button } from "@chakra-ui/react";
import Logo from "components/Logo";
import Card from "components/card";

const initialData: {
  password: string;
  confirm_password: string;
} = {
  password: "",
  confirm_password: "",
};

const appName = process.env.REACT_APP_NAME!;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [formData, setFormData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestSuccess, setIsRequestSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const otpInputRefs = useRef<HTMLInputElement[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = appName + " | Settlement System";

    const appleTitle = document.querySelector(
      "meta[name='apple-mobile-web-app-title']"
    );
    if (appleTitle) appleTitle.setAttribute("content", appName);
  }, []);

  const isFormValid = email !== "";
  const isResetFormValid =
    formData.password.trim() !== "" &&
    formData.confirm_password.trim() !== "" &&
    (otp.length === 6 ? true : false);

  const handleSubmit = async () => {
    setError(null);
    if (isFormValid) {
      setIsLoading(true);
      try {
        const response = await Auth.forgotPassword(email);
        setIsLoading(false);
        setIsRequestSuccess(true);
        console.log(
          "Password reset code sent to email:",
          response.CodeDeliveryDetails
        );
      } catch (error: any) {
        console.error("Error request reset password:", error);
        setError("Email address not recognized");
        setIsLoading(false);
      }
    }
  };

  const handleResetPasswordSubmit = async () => {
    setIsLoading(true);
    setError(null);

    if (formData.password !== formData.confirm_password) {
      setIsLoading(false);
      setError("Passwords do not match");
      return;
    }

    try {
      const otpToVerify = otp.join("");
      await Auth.forgotPasswordSubmit(email, otpToVerify, formData.password);
      setSuccess("Your password has been successfully reset");
    } catch (error) {
      setIsLoading(false);
      if (String(error).includes("InvalidPasswordException")) {
        setError("Invalid password format");
      } else if (String(error).includes("CodeMismatchException")) {
        setError("Invalid verification code");
      } else if (String(error).includes("LimitExceededException")) {
        setError("Attempt limit exceeded, please try again later");
      } else {
        setError("Something went wrong, please contact administrator");
      }
      console.error("Error confirming new password:", error);
    }
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleOTPInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setError(null);
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Focus the next input field if the current one has a value and is not the last one
      if (value && index < 5) {
        otpInputRefs.current[index + 1].focus();
      } else if (index === 5) {
        // Blur the input to remove the cursor when the 6th digit is entered
        otpInputRefs.current[index].blur();
      }
    }
  };

  const handleOTPInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    setError(null);
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        // Move focus to the previous input field if the current one is empty
        otpInputRefs.current[index - 1].focus();
      } else {
        // Clear the current input and keep the focus
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleOTPInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    setError(null);
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    const newOtp = pasteData
      .split("")
      .map((char, i) => (i < 6 ? char : otp[i]));
    setOtp(newOtp);

    // Focus the next input field after paste, but blur the last one
    pasteData.split("").forEach((_, i) => {
      if (i < 5 && pasteData[i]) {
        otpInputRefs.current[i + 1].focus();
      } else if (i === 5) {
        otpInputRefs.current[i].blur();
      }
    });
  };

  const navigateToSignin = async () => {
    localStorage.clear();
    navigate(`/auth/sign-in`);
  };

  return Logo ? (
    <div className="my-16 flex h-full w-full items-center justify-center px-4 sm:px-6">
      <div className="mt-[10vh] w-full max-w-[420px] flex-col items-center md:pl-4 lg:pl-0">
        <div className="mb-12 flex items-center justify-center">
          <Logo light={true} width="229px" />
        </div>

        <Card className="bg-white p-8 shadow-md dark:bg-navy-800 sm:rounded-lg">
          {!isRequestSuccess && (
            <>
              <h2 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
                Reset Your Password
              </h2>
              <p className="mb-6 text-sm text-gray-600 dark:text-white">
                Enter your email address, and we'll send you instructions to
                reset your password.
              </p>

              <InputField
                disabled={isLoading}
                variant="auth"
                extra="mb-4"
                label="Email"                
                required={true}
                placeholder="Enter your email"
                id="email"
                type="text"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {error && (
                <p className="mb-3 text-center text-sm text-red-500">{error}</p>
              )}

              <Button
                onClick={handleSubmit}
                isDisabled={!isFormValid || isLoading}
                isLoading={isLoading}
                loadingText="Please wait..."
                className="btn btn-primary w-full !py-3"
              >
                Send
              </Button>
            </>
          )}

          {isRequestSuccess && success === null && (
            <>
              <h2 className="mb-1 text-2xl font-bold text-navy-700 dark:text-white">
                Create New Password
              </h2>
              <p className="mb-6 text-sm text-gray-600 dark:text-white">
                Please enter your new password and the 6-digit verification code
                we sent to your email.
              </p>

              <InputField
                disabled={isLoading}
                variant="auth"
                extra="mb-4"
                label="New Password"                
                required={true}
                placeholder="Enter new password"
                id="password"
                type="password"
                autoComplete="off"
                value={formData.password}
                onChange={handleInputChange}
              />

              <InputField
                disabled={isLoading}
                variant="auth"
                extra="mb-4"
                label="Confirm Password"                
                required={true}
                placeholder="Re-enter new password"
                id="confirm_password"
                type="password"
                autoComplete="off"
                value={formData.confirm_password}
                onChange={handleInputChange}
              />

              <div className="mb-2 text-center text-sm font-medium text-navy-700 dark:text-white">
                6-Digit Verification Code
              </div>
              <div className="mb-6 flex justify-center gap-2 sm:gap-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      if (el) otpInputRefs.current[index] = el;
                    }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPInputChange(e, index)}
                    onKeyDown={(e) => handleOTPInputKeyDown(e, index)}
                    onPaste={handleOTPInputPaste}
                    className="h-12 w-10 rounded-lg border border-gray-300 text-center shadow-sm outline-none transition-all focus:border-primary-500 focus:ring-1 focus:ring-primary-400"
                  />
                ))}
              </div>

              {error && (
                <p className="mb-4 text-center text-sm text-red-500">{error}</p>
              )}

              <Button
                onClick={handleResetPasswordSubmit}
                isDisabled={!isResetFormValid || isLoading}
                isLoading={isLoading}
                loadingText="Please wait..."
                className="btn btn-primary w-full"
              >
                Submit
              </Button>
            </>
          )}

          {success && (
            <p className="mt-4 text-center text-sm text-primary-500">
              {success}
            </p>
          )}

          <div className="mt-6 text-center">
            <a
              onClick={() => navigateToSignin()}
              className="text-sm text-navy-700 hover:cursor-pointer hover:underline dark:text-white"
            >
              Back to sign in
            </a>
          </div>
        </Card>
      </div>
    </div>
  ) : null;
}
